import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";

export interface Conference {
    conference_id: number;
    slug: string;
    name: string;
    description?: string | null;
    chief?: string | null;
    contact_person?: string | null;
    email?: string | null;
    photo_path?: string | null;
    conference_url?: string | null;
    is_active: boolean;
    created_at?: string;
    created_by?: number | null;
    updated_at?: string;
    updated_by?: number | null;
}

export interface ConferencePayload {
    slug?: string;
    name?: string;
    description?: string | null;
    chief?: string | null;
    contact_person?: string | null;
    email?: string | null;
    conference_url?: string | null;
    is_active?: boolean;
}

export const useAdminConferences = () => {
    return useQuery({
        queryKey: ["admin-conferences"],
        queryFn: async () => {
            const { data } = await axiosClient.get("/private/conferences");
            return data as Conference[];
        },
    });
};

export const useCreateConference = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: ConferencePayload) => {
            const { data } = await axiosClient.post("/private/conferences", payload);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-conferences"] });
        },
    });
};

export const useUpdateConference = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: number; payload: ConferencePayload }) => {
            const { data } = await axiosClient.put(`/private/conferences/${id}`, payload);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-conferences"] });
        },
    });
};

export const useDeleteConference = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: number) => {
            const { data } = await axiosClient.delete(`/private/conferences/${id}`);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-conferences"] });
        },
    });
};

export const useUploadConferencePhoto = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, file }: { id: number; file: File }) => {
            const formData = new FormData();
            formData.append("photo", file);
            const { data } = await axiosClient.post(`/private/conferences/${id}/upload-photo`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-conferences"] });
        },
    });
};
